import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, ArrowRight, Sparkles, AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import Footer from '@/components/Footer';

// Initialize Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;

const planDetails = {
  'recording-highlights': {
    name: 'Recording & Highlights',
    sport: 'Cricket',
    priceValue: '₹99,999',
    priceLabel: 'ONE-TIME INSTALLATION',
    recurring: 'Rs 19,999/month SaaS & maintenance',
    description: 'Perfect for turfs wanting to provide recorded match highlights to players.',
    mainFeatures: [
      'Match Recording',
      'Highlight Generation',
      'Cloud Storage',
      'Mobile App Access',
      'Email Support'
    ],
    fullDescription: 'The Recording & Highlights plan is designed for turfs that want to capture and share match moments with their players. Get professional-grade match recording with automatic highlight generation.',
    detailedFeatures: [
      {
        title: 'Match Recording',
        desc: 'High-quality recording of all matches with multiple camera angles'
      },
      {
        title: 'Highlight Generation',
        desc: 'Automatic AI-powered highlight creation from full match footage'
      },
      {
        title: 'Cloud Storage',
        desc: 'Unlimited cloud storage for all your match recordings'
      },
      {
        title: 'Mobile App Access',
        desc: 'Easy access to all recordings through the GameLens mobile app'
      },
      {
        title: 'Email Support',
        desc: '24/7 email support from our dedicated team'
      },
      {
        title: 'Monthly Maintenance',
        desc: 'Regular system updates and maintenance included'
      }
    ],
    color: '#4FC3F7',
  },
  'stadium-sound': {
    name: 'Stadium Sound',
    sport: 'Cricket',
    priceValue: '₹1,29,999',
    priceLabel: 'ONE-TIME INSTALLATION',
    recurring: 'Rs 19,999/month SaaS & maintenance',
    description: 'Our most popular tier for cricket turfs and academies. Includes Live AI scoring.',
    mainFeatures: [
      'Live AI Scoring',
      'Ball Tracking',
      'Wicket Detection',
      'Real-Time Analytics',
      'Mobile Dashboard',
      'Priority Support'
    ],
    fullDescription: 'The Stadium Sound plan is our most popular offering. It includes live AI scoring, ball tracking, and real-time analytics. Perfect for academies and professional turfs.',
    detailedFeatures: [
      {
        title: 'Live AI Scoring',
        desc: 'Real-time AI-powered scoring as matches happen'
      },
      {
        title: 'Ball Tracking',
        desc: 'Advanced ball trajectory and spin analysis'
      },
      {
        title: 'Wicket Detection',
        desc: 'Automatic detection and analysis of wickets'
      },
      {
        title: 'Real-Time Analytics',
        desc: 'Live player performance analytics and insights'
      },
      {
        title: 'Mobile Dashboard',
        desc: 'Access all data through a comprehensive mobile dashboard'
      },
      {
        title: 'Priority Support',
        desc: 'Priority email and phone support 24/7'
      }
    ],
    badge: 'Most Popular',
    color: '#FF4F7B',
  },
  'cricket-turfs-live-tv': {
    name: 'Cricket-Only Turfs + Live TV Display',
    sport: 'Cricket',
    priceValue: '₹1,59,999',
    priceLabel: 'ONE-TIME INSTALLATION',
    recurring: 'Rs 19,999/month SaaS & maintenance',
    description: 'Premium tier for professional cricket facilities with live TV display integration.',
    mainFeatures: [
      'Stadium Display',
      'Live TV Integration',
      'Multi-Camera Setup',
      'Advanced Analytics',
      'Custom Branding',
      'Dedicated Support'
    ],
    fullDescription: 'The Cricket-Only Turfs + Live TV Display plan is our premium offering. It includes stadium display integration, multi-camera setup, and professional broadcast capabilities.',
    detailedFeatures: [
      {
        title: 'Stadium Display',
        desc: 'Large format display screens for live match visualization'
      },
      {
        title: 'Live TV Integration',
        desc: 'Integration with live TV broadcast systems'
      },
      {
        title: 'Multi-Camera Setup',
        desc: 'Support for multiple synchronized camera angles'
      },
      {
        title: 'Advanced Analytics',
        desc: 'Professional-grade analytics and performance metrics'
      },
      {
        title: 'Custom Branding',
        desc: 'Customize the interface with your turf branding'
      },
      {
        title: 'Dedicated Support',
        desc: 'Dedicated account manager and 24/7 priority support'
      }
    ],
    color: '#FF9A3C',
  },
  'custom-price': {
    name: 'Custom Price',
    sport: 'Tournaments & Venues',
    priceValue: 'Custom Pricing',
    priceLabel: 'CONTACT FOR QUOTE',
    recurring: 'Custom SaaS package',
    description: 'Tailored solutions for large tournaments, stadiums, and international venues.',
    mainFeatures: [
      'Multi-Venue Setup',
      'Custom Infrastructure',
      'Live Broadcast Integration',
      'Tournament Management',
      'VIP Analytics Dashboard',
      'Dedicated Account Manager'
    ],
    fullDescription: 'The Custom Price plan is designed for large-scale tournaments, international stadiums, and professional venues. Get a tailored solution that fits your exact needs.',
    detailedFeatures: [
      {
        title: 'Multi-Venue Setup',
        desc: 'Support for multiple venues with centralized management'
      },
      {
        title: 'Custom Infrastructure',
        desc: 'Infrastructure customized to your specific requirements'
      },
      {
        title: 'Live Broadcast Integration',
        desc: 'Full integration with professional broadcast systems'
      },
      {
        title: 'Tournament Management',
        desc: 'Complete tournament organization and scheduling features'
      },
      {
        title: 'VIP Analytics Dashboard',
        desc: 'Premium analytics dashboard with custom reports'
      },
      {
        title: 'Dedicated Account Manager',
        desc: 'Personal account manager assigned to your organization'
      }
    ],
    color: '#8B5CF6',
  },
};

export default function PlanDetailPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const plan = planDetails[planId];
  const [processingPayment, setProcessingPayment] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handlePayNow = async () => {
    if (!supabase) {
      setErrorMsg('Payment system is not properly configured.');
      return;
    }

    setProcessingPayment(true);
    setErrorMsg('');

    try {
      // Store subscription/payment record in Supabase
      const { error } = await supabase
        .from('subscriptions')
        .insert([
          {
            plan_name: plan.name,
            plan_slug: planId,
            plan_price: plan.priceValue,
            status: 'pending_payment',
            payment_method: 'pay_now',
            created_at: new Date().toISOString(),
          },
        ]);

      if (error) {
        throw error;
      }

      // Show success message and redirect to contact form
      alert(`Payment initiated for ${plan.name}. Please complete your details in the contact form.`);
      navigate('/contact');
    } catch (err) {
      console.error('Payment error:', err);
      setErrorMsg('Failed to process payment. Please try again or contact us directly.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleContactUs = () => {
    // Redirect to contact form with plan info in state
    navigate('/contact', { state: { selectedPlan: plan.name } });
  };

  if (!plan) {
    return (
      <div className="min-h-screen bg-gl-dark flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Plan Not Found</h1>
          <button
            onClick={() => navigate('/pricing')}
            className="px-6 py-3 bg-gradient-brand text-white rounded-xl hover:opacity-90 transition-all"
          >
            Back to Pricing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gl-dark">
      {/* Header */}
      <div className="fixed top-4 left-6 z-50">
        <button
          onClick={() => navigate('/pricing')}
          className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Pricing
        </button>
      </div>

      {/* Hero Section */}
      <section className="py-20 md:py-24 px-6 md:px-12 lg:px-24 bg-gl-dark relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-gl-coral/3 blur-[150px]" />
        
        <div className="max-w-4xl mx-auto relative z-10 pt-8">
          {plan.badge && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-gl-orange/20 to-gl-blue/20 border border-white/10 mb-6">
              <Sparkles className="w-4 h-4 text-gl-orange" />
              <span className="text-sm font-body font-semibold text-white/70">{plan.badge}</span>
            </div>
          )}
          
          <h1 className="font-heading font-bold text-4xl md:text-4xl lg:text-4xl text-white leading-tight mt-4">
            {plan.name}
          </h1>
          
          <p className="text-lg font-body text-white/50 mt-6 max-w-2xl leading-relaxed">
            {plan.fullDescription}
          </p>

          {/* Pricing Info */}
          <div className="mt-12 flex flex-col md:flex-row md:items-end gap-8">
            <div>
              <span className="text-sm font-body font-semibold uppercase tracking-wider text-white/40">{plan.priceLabel}</span>
              <h2 className="font-heading font-bold text-4xl text-white mt-2">{plan.priceValue}</h2>
              <p className="text-white/40 mt-2 font-body">{plan.recurring}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-gl-black">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading font-bold text-3xl text-white mb-12">What's Included</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {plan.detailedFeatures.map((feature, i) => (
              <div key={i} className="dark-glass rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${plan.color}20` }}>
                    <Check className="w-5 h-5" style={{ color: plan.color }} />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-white">{feature.title}</h3>
                    <p className="text-sm font-body text-white/50 mt-1">{feature.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-gl-dark">
        <div className="max-w-4xl mx-auto">
          <div className="dark-glass rounded-2xl p-8 md:p-12 text-center">
            <h2 className="font-heading font-bold text-3xl text-white mb-4">Ready to Get Started?</h2>
            <p className="text-lg font-body text-white/50 mb-8">
              Choose how you'd like to proceed with the {plan.name} plan.
            </p>
            
            {errorMsg && (
              <div className="flex items-center gap-2 text-sm font-body text-gl-coral bg-gl-coral/10 rounded-xl px-4 py-3 mb-4">
                <AlertCircle className="w-4 h-4" />
                {errorMsg}
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center w-full">
              <button
                onClick={handlePayNow}
                disabled={processingPayment}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-brand text-white rounded-xl text-sm sm:text-base font-body font-semibold hover:opacity-90 transition-all shadow-lg inline-flex items-center justify-center gap-2 btn-glow flex-1 sm:flex-none disabled:opacity-60"
              >
                <span>{processingPayment ? 'Processing...' : 'Pay Now'}</span>
                <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5" />
              </button>
              
              <button
                onClick={handleContactUs}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-white/5 text-white border border-white/10 rounded-xl text-sm sm:text-base font-body font-semibold hover:bg-white/8 transition-all inline-flex items-center justify-center gap-2 flex-1 sm:flex-none"
              >
                <span>Contact Us</span>
                <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
